import { Button, Color, Flex, Icon, Input, Select, Text } from "src/elements";
import { rackRuleText } from "../racks";
import { ALL_BAYS_SHELVES, DraftRule } from "../types";
import { colorToRGBA } from "src/utils/colorToRgba";
import { useMemo } from "react";
import { useTranslation } from "src/i18n";
import { SelectOption } from "src/elements/Select/store/types";
import { selectHeight } from "src/elements/Select/store/constants";
import { VariantRestrictionCondition } from "@CommonApi/merchflow/parse-variant-restrictions";

/**
 * A bay/shelf field with an "All" toggle that sets the value to the wildcard
 * `ALL_BAYS_SHELVES` ("*" — every bay or every shelf, backend-supported) so
 * users don't have to type it. Toggling off clears the field back to empty,
 * which the modal blocks from saving until a bay/shelf is entered.
 */
const FieldWithAll = ({
  value,
  placeholder,
  name,
  ariaLabel,
  label,
  onChange,
}: {
  value: string;
  placeholder: string;
  name: string;
  ariaLabel: string;
  label: string;
  onChange: (value: string) => void;
}) => {
  const isAll = value.trim() === ALL_BAYS_SHELVES;

  return (
    <Flex align="center" gap="4px" width="100%">
      <Flex flexGrow={1}>
        <Input value={value} setValue={onChange} placeholder={placeholder} />
      </Flex>
      <Button
        name={name}
        ariaLabel={ariaLabel}
        size="small"
        // Match the paired input's height so the row reads as one control;
        // "secondary" is conveyed by the inverted variant, not by being shorter.
        height={`${selectHeight}px`}
        color="primary"
        variant={isAll ? "default" : "inverted"}
        onClick={() => onChange(isAll ? "" : ALL_BAYS_SHELVES)}
      >
        {label}
      </Button>
    </Flex>
  );
};

interface Props {
  rule: DraftRule;
  /** Shelf choices offered beside the free-text shelf field (racks first). */
  shelfOptions: SelectOption<string>[];
  /** The rule contradicts another rule (see the conflicts panel in the modal). */
  isConflicted?: boolean;
  onChange: (rule: DraftRule) => void;
  onRemove: (id: string) => void;
}

export const RuleRow = ({ rule, shelfOptions, isConflicted, onChange, onRemove }: Props) => {
  const { t } = useTranslation("planogramDelivery");
  const { id, variant, matched, suggestion, bayNumberRule, shelfRule, condition } = rule;
  const conditionOptions = useMemo<SelectOption<VariantRestrictionCondition>[]>(
    () => [
      { label: t("variantRestrictionDescribe.conditionMustHave"), value: "MUST_HAVE" },
      { label: t("variantRestrictionDescribe.conditionMayHave"), value: "CAN_HAVE" },
      { label: t("variantRestrictionDescribe.conditionOnlyThese"), value: "MUST_HAVE_ONLY" },
      { label: t("variantRestrictionDescribe.conditionMustNotHave"), value: "MUST_NOT_HAVE" },
    ],
    [t],
  );

  const needsShelf = !bayNumberRule.trim() || !shelfRule.trim();
  const rackText = rackRuleText(rule);

  return (
    <Flex
      column
      gap="4px"
      padding="8px 10px"
      background={
        isConflicted
          ? colorToRGBA(Color.red, 0.08)
          : matched
            ? undefined
            : colorToRGBA(Color.yellow, 0.12)
      }
    >
      <Flex gap="8px" align="center">
        <Flex flexGrow={1}>
          <Input
            value={variant}
            setValue={(next) => onChange({ ...rule, variant: next, matched: false })}
            placeholder={t("variantRestrictionDescribe.variant")}
          />
        </Flex>

        <Flex width="140px">
          <Select
            options={conditionOptions}
            value={condition}
            setValue={(next) => onChange({ ...rule, condition: next })}
            placeholder={t("variantRestrictionDescribe.condition")}
          />
        </Flex>

        <Flex width="104px">
          <FieldWithAll
            value={bayNumberRule}
            placeholder={t("variantRestrictionDescribe.bay")}
            name="all-bays"
            ariaLabel={t("variantRestrictionDescribe.allBaysAriaLabel")}
            label={t("variantRestrictionDescribe.all")}
            onChange={(next) => onChange({ ...rule, bayNumberRule: next })}
          />
        </Flex>

        <Flex width="118px">
          <FieldWithAll
            value={shelfRule}
            placeholder={t("variantRestrictionDescribe.shelf")}
            name="all-shelves"
            ariaLabel={t("variantRestrictionDescribe.allShelvesAriaLabel")}
            label={t("variantRestrictionDescribe.all")}
            onChange={(next) => onChange({ ...rule, shelfRule: next })}
          />
        </Flex>

        <Button
          name="remove-rule"
          ariaLabel={t("variantRestrictionDescribe.removeRuleAriaLabel")}
          variant="borderless"
          iconRight={{ name: "delete" }}
          onClick={() => onRemove(id)}
        />
      </Flex>

      <Flex gap="8px" align="center" justify="right">
        {rackText && (
          <Text variant="caption2" color={Color.textSecondary}>
            {rackText}
          </Text>
        )}
        <Flex width="236px">
          <Select
            size="small"
            options={shelfOptions}
            value={shelfRule.trim()}
            setValue={(next) => onChange({ ...rule, shelfRule: next })}
            placeholder={t("variantRestrictionDescribe.shelf")}
          />
        </Flex>
      </Flex>

      {!matched && (
        <Flex gap="6px" align="center" padding="0 2px" flexWrap="wrap">
          <Icon name="alert" color={Color.yellow} size={13} />
          <Text variant="caption2" color={Color.yellow}>
            {t(
              suggestion
                ? "variantRestrictionDescribe.noExactVariantMatch"
                : "variantRestrictionDescribe.needsReview",
            )}
          </Text>
          {suggestion && (
            <Button
              name="use-variant-suggestion"
              variant="borderless"
              size="small"
              onClick={() => onChange({ ...rule, variant: suggestion, matched: true })}
            >
              {t("variantRestrictionDescribe.useSuggestion", { suggestion })}
            </Button>
          )}
          {/* Explicit acknowledgement — keep the variant as typed and clear the
              review flag so saving is unblocked (see canApply in the modal). */}
          <Button
            name="acknowledge-variant"
            variant="borderless"
            size="small"
            onClick={() => onChange({ ...rule, matched: true })}
          >
            {t("variantRestrictionDescribe.keepAsTyped")}
          </Button>
        </Flex>
      )}

      {isConflicted && (
        <Flex gap="6px" align="center" padding="0 2px">
          <Icon name="alert" color={Color.red} size={13} />
          <Text variant="caption2" color={Color.red}>
            {t("variantRestrictionDescribe.conflictsWithAnotherRule")}
          </Text>
        </Flex>
      )}

      {matched && needsShelf && (
        <Flex gap="6px" align="center" padding="0 2px">
          <Icon name="alert" color={Color.yellow} size={13} />
          <Text variant="caption2" color={Color.yellow}>
            {t("variantRestrictionDescribe.setBayAndShelf")}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};
